import { useState, useRef, useEffect, forwardRef, memo } from "react";
import { Image, Layer, Stage } from "react-konva";
import Konva from "konva";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import VideoControlBar from "./VideoControlBar";
import Track from "./Track";
import { findValidCoordsBetweenSteps } from "@/util/video-player";
import "./VideoPlayer.scss";
import { cn } from "@/util/functions";

// tracks schema
// tracks = {
//   [trackId]: {
//     [frameNumber]: [x1, y1, x2, y2],
//     ...
//   },
//   ...
// }

// colors schema
// colors = {
//   [trackId]: "red",
//   ...
// }

const playbackRates = [0.5, 1, 1.5, 2, 3, 4];

const VideoPlayer = forwardRef(
  (
    {
      src, // url of the video
      fps,
      step = 5, // used when forward or backward
      frameStep = 1, // this is a frame interval
      videoProps = {},
      startTime = null,
      endTime = null,
      tracks = {}, // check the above tracks schema
      activeTracks = null,
      colors = {}, // check the above colors schema
      ids = {},
      autoZoom = false,
      shouldFocusOnBall = false,
      focusedTrackId = null, // track id which needs auto zooming and tracking
      showLastTrack = null, // this is the trackId of the specific track which needs to be visible all the time, if it doesn't exist on current frame, we will display the last track with different color
      tempRectColor = null, // this color is used when there's no track on the frame
      idVisible = true,
      trackResizable = false,
      zoomButtonsVisible = false,
      zoomArea = null,
      wheelZoomNeedsCtrl = true,
      pbRate = null,
      loop = false,
      timeStamps = [],
      onTrackClick = () => {},
      onMouseDownHandler,
      onMouseMoveHandler,
      onMouseUpHandler,
      keyDownHandler,
      onVideoLoadHandler,
      onTimeUpdateHandler,
      onFrameChange,
      onStageScaleChange,
      onStagePosChange,
      onRatioChange,
      onVideoDomSizeChange,
      onVideoSizeChange,
      onResizeTrack,
      onSetInitialY,
      className = "",
      children = null,
    },
    videoRef
  ) => {
    const videoContainerRef = useRef();
    const layerRef = useRef();
    const progressRef = useRef();

    const [frameTime, setFrameTime] = useState(0);
    const [currentFrame, setCurrentFrame] = useState(0);
    const [ratio, setRatio] = useState(1);

    // Video related States
    const [videoWidth, setVideoWidth] = useState(0);
    const [videoHeight, setVideoHeight] = useState(0);
    const [videoDomWidth, setVideoDomWidth] = useState(0);
    const [videoDomHeight, setVideoDomHeight] = useState(0);
    const [imageY, setImageY] = useState(0);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoDuration, setVideoDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [totalFrameCnt, setTotalFrameCnt] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [muted, setMuted] = useState("muted");

    // Stage related states
    const [stageScale, setStageScale] = useState(1);
    const [stageX, setStageX] = useState(0);
    const [stageY, setStageY] = useState(0);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
      window.addEventListener("resize", handleWindowResize);
      return () => {
        window.removeEventListener("resize", handleWindowResize);
      };
    }, []);

    useEffect(() => {
      if (videoLoaded) {
        window.addEventListener("keydown", onKeyDown);
      }
      return () => {
        window.removeEventListener("keydown", onKeyDown);
      };
    }, [
      videoLoaded,
      isPlaying,
      currentFrame,
      totalFrameCnt,
      playbackRate,
      keyDownHandler,
    ]);

    // We are handling an animation in this hook
    useEffect(() => {
      if (videoLoaded && frameTime) {
        const animCallback = (frame) => {
          if (videoRef.current) {
            const frameNumber = Math.round(
              videoRef.current.currentTime / frameTime
            );
            setCurrentFrame(frameNumber);
          }
        };
        var anim = new Konva.Animation(animCallback, layerRef.current);
        anim.start();
      }
      return () => {
        if (anim) {
          anim.stop();
        }
      };
    }, [videoLoaded, frameTime]);

    useEffect(() => {
      setFrameTime(1 / fps);
    }, [fps]);

    useEffect(() => {
      if (pbRate !== null) setPlaybackRate(pbRate);
    }, [pbRate]);

    useEffect(() => {
      if (videoDuration && frameTime) {
        setTotalFrameCnt(Number(Math.round(videoDuration / frameTime)));
      }
    }, [videoDuration, frameTime]);

    useEffect(() => {
      if (!videoDomWidth || !videoWidth) return;

      const newRatio = videoDomWidth / videoWidth;
      setRatio(newRatio);

      if (zoomArea) {
        const lineHeight1 =
          videoDomHeight * zoomArea.y1 -
          (videoDomHeight - videoHeight * newRatio) / 2;
        const zoomAreaY1 = lineHeight1 / (videoHeight * newRatio);
        const lineHeight2 =
          videoDomHeight * zoomArea.y2 -
          (videoDomHeight - videoHeight * newRatio) / 2;
        const zoomAreaY2 = lineHeight2 / (videoHeight * newRatio);

        const scaledZoomArea = {
          x1: videoWidth * zoomArea.x1,
          x2: videoWidth * zoomArea.x2,
          y1: videoHeight * zoomAreaY1,
          y2: videoHeight * zoomAreaY2,
        };

        const width =
          Math.abs(scaledZoomArea.x1 - scaledZoomArea.x2) * newRatio;
        const height =
          Math.abs(scaledZoomArea.y1 - scaledZoomArea.y2) * newRatio;
        const zoomWidthRatio = videoDomWidth / width;
        const zoomHeightRatio = videoDomHeight / height;
        const zoomScale = Math.min(zoomWidthRatio, zoomHeightRatio);
        setStageScale(zoomScale);
        setStageX(-(scaledZoomArea.x1 * newRatio * zoomScale));
        setStageY(-(scaledZoomArea.y1 * newRatio * zoomScale));
      }
      // imageY is need to place the image at the center of the container.
      setImageY(
        (videoDomHeight - (videoHeight * videoDomWidth) / videoWidth) / 2
      );
      if (typeof onRatioChange === "function") {
        onRatioChange(videoDomWidth / videoWidth);
      }
    }, [videoDomWidth, videoDomHeight, videoWidth, videoHeight, zoomArea]);

    useEffect(() => {
      if (typeof onSetInitialY === "function") {
        onSetInitialY(imageY / ratio);
      }
    }, [imageY, ratio]);

    useEffect(() => {
      if (!frameTime) {
        return;
      }
      setCurrentTime(frameTime * currentFrame);
    }, [frameTime, currentFrame]);

    useEffect(() => {
      if (!autoZoom) return;

      if (!stageScale || !ratio) return;

      // Determine which track to focus on
      let trackToFocus = null;

      // If shouldFocusOnBall is true, always focus on ball track
      if (focusedTrackId) {
        trackToFocus = focusedTrackId;
      } else if (shouldFocusOnBall) {
        trackToFocus = "0";
      } else {
        return; // No track to focus on
      }

      // Check if we have the track to focus on at current frame
      const hasTrackToFocus = tracks[trackToFocus]?.[currentFrame];

      // If we don't have the track to focus on at current frame, return
      if (!hasTrackToFocus) return;

      // If shouldFocusOnBall is true and we have both focused track and ball track,
      // check distance between them to decide which to focus on
      if (
        shouldFocusOnBall &&
        focusedTrackId &&
        tracks[focusedTrackId]?.[currentFrame]
      ) {
        const focusedTrackCenterX =
          (tracks[focusedTrackId][currentFrame][0] +
            tracks[focusedTrackId][currentFrame][2]) /
          2;
        const focusedTrackCenterY =
          (tracks[focusedTrackId][currentFrame][1] +
            tracks[focusedTrackId][currentFrame][3]) /
          2;

        const ballTrackCenterX =
          (tracks["0"][currentFrame][0] + tracks["0"][currentFrame][2]) / 2;
        const ballTrackCenterY =
          (tracks["0"][currentFrame][1] + tracks["0"][currentFrame][3]) / 2;

        // Calculate distance between ball and focused track
        const distance = Math.sqrt(
          Math.pow(focusedTrackCenterX - ballTrackCenterX, 2) +
            Math.pow(focusedTrackCenterY - ballTrackCenterY, 2)
        );

        // If distance is more than 30, focus on ball track, otherwise focus on the focused track
        if (distance > 30) {
          trackToFocus = "0";
        } else {
          trackToFocus = focusedTrackId;
        }
      }

      const MAX_SPEED = 30 * stageScale,
        MIN_SPEED = 0.1;

      const targetX =
        ((tracks[trackToFocus][currentFrame][0] +
          tracks[trackToFocus][currentFrame][2]) /
          2) *
          ratio *
          stageScale +
        stageX;

      const targetY =
        ((tracks[trackToFocus][currentFrame][1] +
          tracks[trackToFocus][currentFrame][3]) /
          2) *
          ratio *
          stageScale +
        stageY +
        ((videoDomHeight - videoHeight * ratio) / 2) * stageScale;

      let deltaX = targetX - videoDomWidth / 2;
      let deltaY = targetY - videoDomHeight / 2;

      const delta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      let speed = Math.min(MAX_SPEED, Math.max(MIN_SPEED, delta * 0.1));

      let angle = Math.atan2(deltaY, deltaX);

      const newX = stageX - Math.cos(angle) * speed;
      const newY = stageY - Math.sin(angle) * speed;

      if (newX) setStageX(newX);
      if (newY) setStageY(newY);
    }, [currentFrame]);

    useEffect(() => {
      if (typeof onFrameChange === "function") {
        onFrameChange(currentFrame);
      }

      if (startTime === null && endTime === null) return;

      if (startTime !== null && currentFrame * frameTime < startTime)
        videoRef.current.currentTime = startTime;
      if (endTime !== null && currentFrame * frameTime > endTime) {
        if (loop) {
          videoRef.current.currentTime = startTime;
          return;
        }
        videoRef.current.currentTime = endTime;
        videoRef.current.pause();
      }
    }, [currentFrame, frameTime]);

    useEffect(() => {
      if (startTime !== null) videoRef.current.currentTime = startTime;
    }, [startTime, endTime]);

    useEffect(() => {
      if (typeof onStageScaleChange === "function") {
        onStageScaleChange(stageScale);
      }
    }, [stageScale]);

    useEffect(() => {
      if (typeof onStagePosChange === "function") {
        onStagePosChange(stageX, stageY);
      }
    }, [stageX, stageY]);

    useEffect(() => {
      if (typeof onVideoDomSizeChange === "function") {
        onVideoDomSizeChange(videoDomWidth, videoDomHeight);
      }
    }, [videoDomWidth, videoDomHeight]);

    useEffect(() => {
      if (typeof onVideoSizeChange === "function") {
        onVideoSizeChange(videoWidth, videoHeight);
      }
    }, [videoWidth, videoHeight]);

    useEffect(() => {
      if (videoRef.current) videoRef.current.playbackRate = playbackRate;
    }, [playbackRate]);

    useEffect(() => {
      if (videoProps.muted !== null && videoProps.muted !== undefined)
        setMuted(videoProps.muted);
    }, [videoProps]);

    useEffect(() => {
      if (!videoRef.current || muted === null || muted === undefined) {
        return;
      }
      videoRef.current.muted = muted;
    }, [muted]);

    function handleWindowResize() {
      if (videoContainerRef.current) {
        setVideoDomWidth(videoContainerRef.current?.offsetWidth);
        setVideoDomHeight(videoContainerRef.current?.offsetHeight);
      }
    }

    const onKeyDown = (event) => {
      if (event.key === "f") {
        handleNextFrame();
      } else if (event.key === "d") {
        handlePrevFrame();
      } else if (event.key === "v") {
        handleNextStep();
      } else if (event.key === "c") {
        handlePrevStep();
      } else if (event.key === " ") {
        event.preventDefault();
        togglePlay();
      } else if (event.key === "x") {
        event.preventDefault();
        const nextRateIndex =
          playbackRates.indexOf(playbackRate) + 1 >= playbackRates.length
            ? playbackRates.length - 1
            : playbackRates.indexOf(playbackRate) + 1;
        setPlaybackRate(playbackRates[nextRateIndex]);
      } else if (event.key === "z") {
        event.preventDefault();
        const prevRateIndex =
          playbackRates.indexOf(playbackRate) - 1 < 0
            ? 0
            : playbackRates.indexOf(playbackRate) - 1;
        setPlaybackRate(playbackRates[prevRateIndex]);
      }
      if (typeof keyDownHandler === "function") {
        keyDownHandler(event);
      }
    };

    // #region Video Navigation Handlers
    const forwardOrBackward = (frameCnt) => {
      console.log({ frameCnt });
      const videoElement = videoRef.current;
      if (
        videoElement &&
        currentFrame + frameCnt >= 0 &&
        currentFrame + frameCnt <= totalFrameCnt
      ) {
        videoElement.currentTime = frameTime * (currentFrame + frameCnt);
        setCurrentFrame(currentFrame + frameCnt);
      }
    };
    const handleNextFrame = () => {
      forwardOrBackward(1);
    };
    const handlePrevFrame = () => {
      forwardOrBackward(-1);
    };
    const handleNextStep = () => {
      forwardOrBackward(step);
    };
    const handlePrevStep = () => {
      forwardOrBackward(-step);
    };
    const goFirst = () => {
      if (videoRef.current) videoRef.current.currentTime = 0;
    };
    const goLast = () => {
      if (videoRef.current) videoRef.current.currentTime = videoDuration;
    };
    const handleChangeSpeed = (e) => {
      setPlaybackRate(Number(e.target.value));
    };
    const onPlayerProgressClick = (e) => {
      const rect = progressRef.current?.getBoundingClientRect();

      const pos = (e.pageX - rect.left) / progressRef.current?.offsetWidth;

      videoRef.current.currentTime = pos * videoDuration;
      const frameNumber = Math.round((pos * videoDuration) / frameTime);
      setCurrentFrame(frameNumber);
    };
    // #endregion Video Navigation Handlers

    const zoomIn = () => {
      setStageScale(stageScale + 0.2);
    };
    const zoomOut = () => {
      setStageScale(stageScale - 0.2);
    };

    const togglePlay = async () => {
      try {
        if (isPlaying) {
          await videoRef.current.pause();
        } else {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Video playback error:", error);
        }
      }
    };

    const onVideoLoad = () => {
      setVideoDomWidth(videoContainerRef.current?.offsetWidth);
      setVideoDomHeight(videoContainerRef.current?.offsetHeight);
      setVideoWidth(videoRef.current?.videoWidth);
      setVideoHeight(videoRef.current?.videoHeight);
      setVideoLoaded(true);
      setVideoDuration(videoRef.current.duration);
      if (typeof onVideoLoadHandler === "function") {
        onVideoLoadHandler();
      }
    };

    const onTimeUpdate = () => {
      if (typeof onTimeUpdateHandler === "function") {
        onTimeUpdateHandler();
      }
    };

    const onPlay = () => {
      setIsPlaying(true);
    };

    const onPause = () => {
      setIsPlaying(false);
    };

    const toggleMute = () => {
      setMuted(muted === false ? "muted" : false);
    };

    // #region Stage Event Handlers
    const onMouseDown = (e) => {
      if ((e.evt.button === 0 && e.evt.ctrlKey) || e.evt.button === 2) {
        e.target.getStage().startDrag();
      }
      if (typeof onMouseDownHandler === "function") {
        onMouseDownHandler(e);
      }
    };

    const onMouseMove = (e) => {
      if (typeof onMouseMoveHandler === "function") {
        onMouseMoveHandler(e);
      }
    };

    const onMouseUp = (e) => {
      if (e.evt.button === 2) {
        if (dragging) e.target.getStage().stopDrag();
        return;
      }
      if (typeof onMouseUpHandler === "function") {
        onMouseUpHandler(e);
      }
    };

    const onContextMenu = (e) => {
      e.evt.preventDefault();
    };

    const onDragStart = (e) => {
      setDragging(true);
    };

    const onDragEnd = (e) => {
      const stage = e.target.getStage();
      setStageX(stage.x());
      setStageY(stage.y());
      setDragging(false);
    };

    const onWheel = (e) => {
      if (wheelZoomNeedsCtrl && !e.evt.ctrlKey) return;

      e.evt.preventDefault();

      const scaleBy = 1.1;
      const stage = e.target.getStage();
      const oldScale = stage.scaleX();
      const mousePointTo = {
        x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
        y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
      };

      const newScale =
        e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

      setStageScale(newScale);
      setStageX(
        -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale
      );
      setStageY(
        -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale
      );
    };
    // #endregion Stage Event Handlers

    return (
      <div className={cn("video-player-container", className)}>
        <div
          ref={videoContainerRef}
          className="position-relative w-100"
          style={{
            aspectRatio: "4 / 2",
            backgroundColor: "black",
          }}
        >
          {!videoLoaded ? (
            <div className="w-100 top-0 bottom-0 position-absolute d-flex justify-content-center align-items-center">
              <Spinner animation="border" role="status" variant="light">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : null}
          <video
            className="invisible"
            ref={videoRef}
            width="100%"
            src={src}
            controls={false}
            autoPlay={false}
            muted={muted}
            preload="auto"
            loop
            crossOrigin="anonymous"
            onLoadedData={onVideoLoad}
            onTimeUpdate={onTimeUpdate}
            onPlay={onPlay}
            onPause={onPause}
            {...videoProps}
          />
        </div>
        <Stage
          className="video-player-stage"
          width={videoDomWidth}
          height={videoDomHeight}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stageX}
          y={stageY}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onContextMenu={onContextMenu}
          onWheel={onWheel}
        >
          <Layer ref={layerRef}>
            {videoLoaded ? (
              <Image
                x={0}
                y={zoomArea ? 0 : imageY}
                width={videoDomWidth}
                height={(videoDomWidth * videoHeight) / videoWidth}
                image={videoRef.current}
              />
            ) : null}
            {Object.keys(tracks)
              .filter(
                (trackId) =>
                  ((activeTracks !== null &&
                    activeTracks.length > 0 &&
                    activeTracks.includes(String(trackId))) ||
                    activeTracks === null) &&
                  findValidCoordsBetweenSteps(
                    tracks[trackId],
                    currentFrame,
                    frameStep,
                    String(showLastTrack) === String(trackId)
                  )
              )
              .map((trackId) => {
                const crds = findValidCoordsBetweenSteps(
                  tracks[trackId],
                  currentFrame,
                  frameStep,
                  String(showLastTrack) === String(trackId)
                );
                const coords = crds.map((val, index) => {
                  // initial rendering y position is at the half of the videoHeight.
                  if (!zoomArea && [1, 3].includes(index)) {
                    return val * ratio + imageY;
                  } else {
                    return val * ratio;
                  }
                });
                return (
                  <Track
                    key={trackId}
                    trackId={trackId}
                    visibleTrackId={ids[trackId]}
                    coords={coords}
                    stageScale={stageScale}
                    rectColor={colors[trackId]}
                    onTrackClick={onTrackClick}
                    idVisible={idVisible}
                    trackResizable={trackResizable}
                    onResizeTrack={onResizeTrack}
                    isTemp={tracks[trackId][currentFrame] ? false : true}
                    tempRectColor={tempRectColor}
                  />
                );
              })}
            {children}
          </Layer>
        </Stage>
        {zoomButtonsVisible ? (
          <div className="video-player-stage-controls">
            <Button variant="success" size="sm" onClick={zoomIn}>
              <FontAwesomeIcon icon={["fas", "plus"]} />
            </Button>
            <Button variant="success" size="sm" onClick={zoomOut}>
              <FontAwesomeIcon icon={["fas", "minus"]} />
            </Button>
          </div>
        ) : null}
        <div className="video-player-control-bar">
          <VideoControlBar
            goFirst={goFirst}
            handlePrevStep={handlePrevStep}
            handlePrevFrame={handlePrevFrame}
            handleNextFrame={handleNextFrame}
            handleNextStep={handleNextStep}
            goLast={goLast}
            playbackRate={playbackRate}
            playbackRates={playbackRates}
            handleChangeSpeed={handleChangeSpeed}
            isPlaying={isPlaying}
            togglePlay={togglePlay}
            muted={muted}
            toggleMute={toggleMute}
            progress={progressRef}
            videoDuration={videoDuration}
            currentTime={currentTime}
            startTime={startTime}
            endTime={endTime}
            timeStamps={timeStamps}
            onPlayerProgressClick={onPlayerProgressClick}
          />
        </div>
      </div>
    );
  }
);

export default memo(VideoPlayer);
